import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
interface Conjunction {
  CDM_ID?: string;
  pName: string;
  sName: string;
  dca: number;
  poc: number;
  tcaTime: string;
}

interface GlobalConjunctionsSectionProps {
  filteredConjunctions: Conjunction[];
  isGlobalLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onRowClick: (cdmId: string) => void;
  onLoadMore: () => void;
}

const GlobalConjunctionsSection: React.FC<GlobalConjunctionsSectionProps> = ({
  filteredConjunctions,
  isGlobalLoading,
  hasNextPage,
  isFetchingNextPage,
  onRowClick,
  onLoadMore,
}) => {
  const [displayedCount, setDisplayedCount] = useState(3);

  const formatTcaTime = useCallback((tcaTime: string) => {
    const cleanedTime = tcaTime.replace('\n', ' ').split('.')[0];
    const parsedDate = dayjs(cleanedTime);
    return parsedDate.format('MMM DD, HH:mm:ss');
  }, []);

  const handleLoadMore = () => {
    if (displayedCount < filteredConjunctions.length) {
      setDisplayedCount(prev => prev + 3);
    } else {
      onLoadMore();
    }
  };

  const displayedConjunctions = filteredConjunctions.slice(0, displayedCount);
  const canLoadMore =
    displayedCount < filteredConjunctions.length || hasNextPage;

  return (
    <div className='bg-white rounded-2xl p-4 w-full max-w-[1400px] shadow-lg'>
      <div className='flex justify-between items-center pt-4 pb-8 flex-wrap gap-4'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br from-red-100 to-red-200'>
            🌍
          </div>
          <p className='text-lg font-bold text-slate-800'>Top Conjunctions</p>
        </div>
      </div>

      {isGlobalLoading ? (
        <div className='text-center py-10 text-slate-500'>
          Loading conjunctions...
        </div>
      ) : filteredConjunctions.length === 0 ? (
        <div className='text-center py-10 text-slate-500'>
          No conjunctions match the current filter.
        </div>
      ) : (
        <div className='flex flex-col gap-2'>
          <div className='flex flex-col gap-2'>
            {displayedConjunctions.map((conjunction, index) => {
              return (
                <div
                  key={conjunction.CDM_ID || index}
                  className='bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 cursor-pointer transition-colors hover:bg-slate-50 hover:border-slate-300'
                  onClick={() =>
                    conjunction.CDM_ID && onRowClick(conjunction.CDM_ID)
                  }
                >
                  <div className='flex justify-between items-center py-2 border-b border-slate-200'>
                    <div className='flex gap-4'>
                      <div className='flex flex-col'>
                        <p className='text-[11px] text-slate-500 uppercase mb-0.5'>
                          Primary
                        </p>
                        <p className='text-sm font-semibold text-slate-800'>
                          {conjunction.pName}
                        </p>
                      </div>
                      <div className='flex flex-col'>
                        <p className='text-[11px] text-slate-500 uppercase mb-0.5'>
                          Secondary
                        </p>
                        <p className='text-sm font-semibold text-slate-800'>
                          {conjunction.sName}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='grid grid-cols-[1.6fr_1fr_1fr] items-center py-2 whitespace-nowrap gap-4'>
                    <div className='flex flex-col'>
                      <p className='text-[11px] text-slate-500 uppercase mb-0.5'>
                        TCA(UTC)
                      </p>
                      <p className='text-sm font-medium text-slate-800'>
                        {formatTcaTime(conjunction.tcaTime)}
                      </p>
                    </div>
                    <div className='flex flex-col'>
                      <p className='text-[11px] text-slate-500 uppercase mb-0.5'>
                        DCA(KM)
                      </p>
                      <p className='text-sm font-medium text-slate-800'>
                        {conjunction.dca.toFixed(3)}
                      </p>
                    </div>
                    <div className='flex flex-col'>
                      <p className='text-[11px] text-slate-500 uppercase mb-0.5'>
                        POC_MAX
                      </p>
                      <p className='text-sm font-medium text-slate-800'>
                        {conjunction.poc
                          ? conjunction.poc.toExponential(2)
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {canLoadMore && (
            <div className='text-center'>
              <button
                className='h-[48px] px-4 w-full bg-[#fccb16] border-none text-slate-800 rounded-[4px] cursor-pointer transition-all text-sm font-medium hover:bg-[#ffd84d] hover:-translate-y-0.5 disabled:bg-slate-400 disabled:cursor-not-allowed'
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More Conjunctions'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalConjunctionsSection;
